/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';
/* eslint-disable */
import { ActionButton, CustomizerContext, mergeStyles, mergeStyleSets, Persona, Spinner } from '@fluentui/react';
import './TeamTagCardView.scss';
import { TeamTagMember, TeamTagResult } from '../../../service/gen';
import { getThemedIcon, IconName } from '../../../resources/icons/Icons';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { stringFormat } from '../../../utilities/Utils';
import { CoreColors, FontSizes } from '../../../styles/teams';

export interface TagCardViewProps {
    tagCardData?: TeamTagResult;
}

export const TeamTagCardView: React.FC<TagCardViewProps> = ({ tagCardData }: TagCardViewProps): JSX.Element => {
    const teamTagIcon = getThemedIcon(IconName.teamTagIcon);
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;

    const tagMembers = tagCardData?.Members ?? [];
    const tagDescriptionDoesNotFitInCard = true;

    const [expanded, setExpanded] = React.useState(false);

    const classNames = mergeStyleSets({
        infoTitle: mergeStyles('title', {
            fontSize: FontSizes.size14,
            color: theme.palette.black,
        }),
        infoSign: mergeStyles('sign', {
            fontSize: FontSizes.size12,
            background: theme.palette.themeLight,
            color: theme.palette.themePrimary,
        }),
        infoMemberCount: mergeStyles('member-count', {
            fontSize: FontSizes.size12,
            color: theme.palette.black,
        }),
        expandableSection: mergeStyles('expandable-section', {
            borderTop: `1px solid ${CoreColors.appGray10}`,
            color: theme.palette.black,
        }),
        tagDescription: mergeStyles('tag-description', {
            borderBottom: `1px solid ${theme.palette.neutralLight}`,
        }),
        seeMoreRoot: {
            padding: 0,
        },
        seeMoreLabel: {
            fontSize: FontSizes.size12,
            margin: 0,
        },
        sectionLabel: mergeStyles('section-label', {
            color: theme.palette.black,
        }),
        personaPrimaryText: {
            color: theme.palette.black,
        },
    });

    return (
        <div className='tag-card'>
            <div className='main-section'>
                {!tagCardData ? (
                    <Spinner className='loading'></Spinner>
                ) : (
                    <div className='main-info'>
                        <div className='group-picture'>{teamTagIcon}</div>
                        <div className='info'>
                            <span className={classNames.infoTitle}>{tagCardData.DisplayName}</span>
                            <div className='sign-container'>
                                <span className={classNames.infoSign}>{loc.strings.teamtagcard_sign}</span>
                            </div>
                            <span className={classNames.infoMemberCount}>
                                {stringFormat(loc.strings.teamtagcard_members, {
                                    totalNumber: `${tagCardData.MemberCount}`,
                                })}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className={classNames.expandableSection}>
                <div className='expandable-section-container simple-scrollbar'>
                    {!tagCardData ? (
                        <Spinner className='loading'></Spinner>
                    ) : (
                        <div>
                            {tagCardData.Description && (
                                <div className={classNames.tagDescription}>
                                    <div className={classNames.sectionLabel}>
                                        <span className='bold-label'>{loc.strings.teamtagcard_description}</span>
                                    </div>
                                    <div className='tag-description-expandable'>
                                        <span
                                            className={`tag-description-content ${
                                                expanded ? 'description-expanded' : ''
                                            }`}>
                                            {tagCardData.Description}
                                        </span>
                                        {tagDescriptionDoesNotFitInCard && (
                                            <ActionButton
                                                aria-label={
                                                    expanded
                                                        ? loc.strings.teamtagcard_description_less
                                                        : loc.strings.teamtagcard_description_more
                                                }
                                                text={
                                                    expanded
                                                        ? loc.strings.teamtagcard_description_less
                                                        : loc.strings.teamtagcard_description_more
                                                }
                                                styles={{
                                                    root: classNames.seeMoreRoot,
                                                    label: classNames.seeMoreLabel,
                                                }}
                                                onClick={(): void => {
                                                    setExpanded(!expanded);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className='members'>
                                <div className={classNames.sectionLabel}>
                                    <span className='bold-label'>
                                        {stringFormat(loc.strings.teamtagcard_detail_members, {
                                            totalNumber: `${tagCardData.MemberCount}`,
                                        })}
                                    </span>
                                </div>
                                <div className='member-list' role='list'>
                                    {tagMembers.map(
                                        (m: TeamTagMember): JSX.Element => (
                                            <div key={m.UserId} className='member-item'>
                                                <Persona
                                                    coinSize={28}
                                                    text={m.DisplayName}
                                                    imageUrl={m.Thumbnail ?? undefined}
                                                    styles={{
                                                        primaryText: classNames.personaPrimaryText,
                                                    }}
                                                />
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
